import { formatNumberWithDecimalPlaces } from "@/Utils/formatFunctions";
import { expandedTenantDetailsColumns } from "@/Utils/tableColumns/tenantDetailsColumns";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import Piechart from "@/components/Piechart";
import { Table } from "@/components/Table";
import { TableRow } from "@/components/Table/TableRow";
import { Card } from "@/components/pageCards/card";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import React, { useMemo } from "react";

import { sum } from "lodash";

import { CollapsableTableDataInterface } from "../../customerDetails.interface";

const SORTED_KEY = "sorted";
const UNSORTED_KEY = "unsorted";

const chartConfig = {
  enableArcLabels: true,
  arcLabelsTextColor: "#FFFFFF",
  arcLabel: (d: any) =>
    `${formatNumberWithDecimalPlaces(d.data.percent * 100, 1)}%`,
  theme: {
    labels: {
      text: {
        fontSize: 14,
      },
    },
  },
};

type Props = {
  tenantTableData: CollapsableTableDataInterface;
};

export const DetailsTable: React.FC<Props> = ({ tenantTableData }) => {
  const { t } = useTrans();
  const { terminalConfig } = useConfigService();

  const recyclingRateData = useMemo(
    () => [
      {
        id: SORTED_KEY,
        label: t("statisticsLabels:sorted"),
        value: tenantTableData.totalWeight - tenantTableData.residualWaste,
        color: terminalConfig?.sortedColor || "#467432",
      },
      {
        id: UNSORTED_KEY,
        label: t("statisticsLabels:unsorted"),
        value: tenantTableData.residualWaste,
        color: terminalConfig?.unsortedColor || "#000000",
      },
    ],
    [tenantTableData, terminalConfig, t],
  );

  const totalRowValues = useMemo(
    () => ({
      id: "total",
      wasteType: t("statisticsLabels:total"),
      throws: sum(tenantTableData.details.map((ws) => ws.throws)),
      totalWeight: sum(tenantTableData.details.map((ws) => ws.totalWeight)),
      share: 100,
    }),
    [t, tenantTableData],
  );

  const view = useMemo(() => {
    if (!tenantTableData.throws) {
      return (
        <div className="flex h-full w-full min-w-[650px] flex-col justify-stretch">
          <EmptyView type="noDataForInterval" className="h-full" />
        </div>
      );
    }

    return (
      <div
        className={`col-span-${expandedTenantDetailsColumns.length} min-w-[650px] px-4 pb-4`}
      >
        <div className="relative flex flex-row">
          <div className="w-1/2">
            <Piechart
              data={recyclingRateData}
              hideLegend
              chartConfig={chartConfig}
            />
          </div>
          <div className="m-auto w-1/2 text-center">
            <h1 className="font-signifier text-4xl">
              {tenantTableData.recyclingRate}%
            </h1>
            <h3 className="font-semibold">
              {t("circularityLabels:recyclingRate")}
            </h3>
          </div>
        </div>
        <h6 className="px-2 pb-2 text-start text-sm font-semibold text-black">
          {t("statisticsLabels:breakdownOnWasteTypes")}
        </h6>
        <Table
          columns={expandedTenantDetailsColumns}
          data={tenantTableData.details}
          headerClassName="bg-hover-color z-0 "
          rightAlign
          tableClassName="max-h-[102px] overflow-y-auto"
          className="mb-2"
        />
        <TableRow
          values={totalRowValues}
          columns={expandedTenantDetailsColumns}
          isCollapsible={false}
          rowClassName="bg-hover-color hover:bg-grey1"
          hasDelete={false}
          deleteFunction={() => {}}
          isLast
        />
      </div>
    );
  }, [recyclingRateData, t, totalRowValues, tenantTableData]);

  return (
    <Card
      title="statisticsLabels:wasteStreamDetails"
      overflow="none"
      hasXContentPadding={false}
      hasYContentPadding={false}
    >
      {view}
    </Card>
  );
};

export default DetailsTable;
